import { useEffect, useState } from "react";
import { ApiError, fetchSignalDecay, type DecayPipelineResult, type SignalDecayDto } from "../api";
import { FieldHelp } from "../components/FieldHelp";
import { PageHelper } from "../components/PageHelper";
import { exportRowsAsCsv } from "../exportCsv";
import { sortGlyph, useSort } from "../hooks/useSort";

function pct(v: number | null | undefined, digits = 3): string {
  if (v === null || v === undefined) return "—";
  return `${(v * 100).toFixed(digits)}%`;
}

function TestRow({ name, verdict, detail }: { name: string; verdict: boolean | null; detail: string }) {
  const cls = verdict === null ? "xs-chip xs-chip-mute" : verdict ? "xs-chip xs-chip-ok" : "xs-chip xs-chip-bad";
  const label = verdict === null ? "not run" : verdict ? "PASS" : "did not clear";
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "baseline", padding: "6px 0", borderBottom: "1px solid var(--rule)" }}>
      <span className={cls} style={{ minWidth: 92, textAlign: "center" }}>{label}</span>
      <div>
        <b>{name}</b>
        <div className="page-sub" style={{ fontSize: ".78rem" }}>{detail}</div>
      </div>
    </div>
  );
}

function VerdictSequence({ r }: { r: DecayPipelineResult }) {
  const t = r.tests;
  const t1 = t.test1_day1_positive;
  const t2 = t.test2_validated_horizons;
  const t3 = t.test3_slope_vs_flat;
  const t4 = t.test4_beats_random_entry;
  const stoppedAt = t.stopped_at as string | null;

  return (
    <div>
      <TestRow
        name="1. Day-1 marginal edge > 0"
        verdict={stoppedAt ? (stoppedAt === "test1" ? false : true) : t1?.significant ?? null}
        detail={t1 ? `mean=${pct(t1.observed_mean)}, n_dates=${t1.n_dates}, p=${t1.p?.toFixed(4) ?? "—"} (sign-flip permutation)` : "not run"}
      />
      {t2 && (
        <TestRow
          name="2. Marginal edge > 0 at a registry-validated horizon"
          verdict={stoppedAt === "test1" ? null : stoppedAt === "test2" ? false : t2.pass}
          detail={
            Object.entries(t2.checkpoints as Record<string, any>)
              .map(([h, v]: [string, any]) => `h=${h}d: p_fdr=${v.p_fdr?.toFixed(3) ?? "—"}`)
              .join(" · ") + ` · age=7d (cross-ref only, not a registered horizon): p=${t2.age_7_cross_reference_only?.p?.toFixed(3) ?? "—"}`
          }
        />
      )}
      {t3 && (
        <TestRow
          name="3. Decay slope distinguishable from flat"
          verdict={stoppedAt === "test1" || stoppedAt === "test2" ? null : stoppedAt === "test3" ? false : t3.significant}
          detail={`slope=${t3.observed_slope?.toExponential(2) ?? "—"}, p=${t3.p?.toFixed(4) ?? "—"} (age-label-shuffle permutation, ${t3.ages_used?.length ?? 0} usable ages)`}
        />
      )}
      {t4 && (
        <TestRow
          name="4. Beats random-entry control"
          verdict={stoppedAt && stoppedAt !== "test4" && stoppedAt !== null ? null : t4.significant}
          detail={`p=${t4.p?.toFixed(4) ?? "—"} vs. count-matched random entry dates, checkpoint age=${t4.checkpoint_age}d`}
        />
      )}
      <div className="page-sub" style={{ marginTop: 10 }}>
        {stoppedAt
          ? <>Stopped at <b>{stoppedAt}</b> -- no shape classification attempted past this point. This is the honest "accumulating" state, not a failure of the method.</>
          : <>All four tests cleared. Shape: <b>{r.shape?.classification}</b>{r.shape?.half_life_days != null && <> -- half-life ≈ {r.shape.half_life_days.toFixed(1)} trading days</>}</>}
      </div>
    </div>
  );
}

function CurveTable({ r }: { r: DecayPipelineResult }) {
  const rows = Object.values(r.curve).sort((a, b) => a.age - b.age);
  const { sorted, sortKey, sortDir, toggleSort } = useSort(rows, (row, key) => (row as any)[key], "age");
  const cols: { key: string; label: string }[] = [
    { key: "age", label: "Age (trading days)" },
    { key: "marginal_mean", label: "Marginal excess" },
    { key: "cumulative_mean", label: "Cumulative excess" },
    { key: "n_dates", label: "n dates" },
  ];
  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
        <button className="xs-btn" onClick={() => exportRowsAsCsv(rows as any, cols, `signal_decay_${r.label}.csv`)}>↓ CSV</button>
      </div>
      <table className="xs-table">
        <thead>
          <tr>
            <th className="clickable" onClick={() => toggleSort("age")}>Age {sortGlyph("age", sortKey, sortDir)}</th>
            <th className="clickable" onClick={() => toggleSort("marginal_mean")}>Marginal excess (±95% CI) {sortGlyph("marginal_mean", sortKey, sortDir)}</th>
            <th className="clickable" onClick={() => toggleSort("cumulative_mean")}>Cumulative excess (±95% CI) {sortGlyph("cumulative_mean", sortKey, sortDir)}</th>
            <th className="clickable" onClick={() => toggleSort("n_dates")}>n dates {sortGlyph("n_dates", sortKey, sortDir)}</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((p) => (
            <tr key={p.age}>
              <td>{p.age}d</td>
              <td className="xs-num">
                {pct(p.marginal_mean)}
                {p.marginal_ci_low !== null && <span className="page-sub" style={{ fontSize: ".72rem" }}> [{pct(p.marginal_ci_low)}, {pct(p.marginal_ci_high)}]</span>}
              </td>
              <td className="xs-num">
                {pct(p.cumulative_mean)}
                {p.cumulative_ci_low !== null && <span className="page-sub" style={{ fontSize: ".72rem" }}> [{pct(p.cumulative_ci_low)}, {pct(p.cumulative_ci_high)}]</span>}
              </td>
              <td className="xs-num">{p.n_dates}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

export function SignalDecayPage({ token, onUnauthorized }: { token: string; onUnauthorized: () => void }) {
  const [data, setData] = useState<SignalDecayDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSignalDecay(token)
      .then(setData)
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) return onUnauthorized();
        setError(err instanceof ApiError ? err.message : "request failed");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="shell" style={{ maxWidth: 1040 }}>
      <div className="page-head">
        <h1>Signal Half-Life <FieldHelp term="signal_half_life" /></h1>
        <div className="page-sub">How fast does the blended scorecard signal's edge decay from its fire date?</div>
      </div>
      <PageHelper>
        Every BUY event (blended cross-segment signal &gt; 0, from the real scorecard_fire history) is
        tracked forward against its benchmark (sector index where one has a confident match, NIFTY
        otherwise) for up to 20 trading days. Four pre-registered null tests run IN ORDER, stopping at
        the first one that doesn't clear -- a stop is reported as "accumulating," not a failure. Only
        once all four clear does the curve get a shape classification and, if it's a genuine monotone
        decay, a half-life number. This is a weekly-refreshed cache, not computed live.
      </PageHelper>

      {error && <div className="card xs-card"><div className="xs-error">{error}</div></div>}
      {!data && !error && <div className="card xs-card"><div className="page-sub">Loading…</div></div>}

      {data && (
        <>
          <div className="card xs-card">
            <div className="xs-honest-banner">{data.small_n_caveat}</div>
            <p className="page-sub">
              Cache computed {data.cache_computed_at} ({data.cache_days_since_computed} day
              {data.cache_days_since_computed === 1 ? "" : "s"} ago). {data.cache_cadence_comparison}
            </p>
          </div>

          <div className="card xs-card">
            <h2>Full sample -- {data.n_events} BUY events</h2>
            <VerdictSequence r={data} />
          </div>

          <div className="card xs-card">
            <h3 className="xs-subhead">Decay curve (full sample)</h3>
            <CurveTable r={data} />
          </div>

          <div className="card xs-card">
            <h2>Clean-benchmark-subset robustness check</h2>
            <p className="page-sub">
              Same pipeline, restricted to the {data.benchmark_heterogeneity.n_clean} events whose stock
              had a confident sector-index benchmark match (vs. {data.benchmark_heterogeneity.n_fallback} events
              that fell back to broad NIFTY). Does the headline verdict hold on this cleaner subset alone?
            </p>
            <VerdictSequence r={data.clean_benchmark_subset} />
            <h3 className="xs-subhead">NIFTY-fallback events by sector (residual sector-beta exposure)</h3>
            <div className="xs-exclusion-grid">
              {Object.entries(data.benchmark_heterogeneity.fallback_events_by_sector).map(([sector, count]) => (
                <div key={sector} className="xs-exclusion-cell">
                  <span className="xs-exclusion-count">{count}</span>
                  <span className="xs-exclusion-label">{sector.replace(/_/g, " ")}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="card xs-card">
            <h2>Attrition &amp; residual limitations</h2>
            <dl className="xs-dl">
              <dt>Total gated fire dates</dt><dd>{data.attrition.n_total_fire_dates}</dd>
              <dt>Surviving dates at age 1 (≥5-event filter)</dt><dd>{data.attrition.n_surviving_dates_at_age1}</dd>
              <dt>Fraction of events retained</dt><dd>{pct(data.attrition.fraction_events_retained_age1, 1)}</dd>
              {data.attrition.regime_split_early_vs_late && (
                <>
                  <dt>Regime split (early vs. late half)</dt>
                  <dd>{data.attrition.regime_split_early_vs_late.early} / {data.attrition.regime_split_early_vs_late.late}
                    {" "}(midpoint {data.attrition.regime_split_early_vs_late.midpoint_date})</dd>
                </>
              )}
              <dt>Distinct stocks firing BUY</dt><dd>{data.same_stock_across_dates_overlap.n_distinct_stocks}</dd>
              <dt>Median / max fires per stock</dt>
              <dd>{data.same_stock_across_dates_overlap.median_fires_per_stock} / {data.same_stock_across_dates_overlap.max_fires_per_stock}</dd>
            </dl>
            <p className="page-sub">{data.same_stock_across_dates_overlap.note}</p>
          </div>
        </>
      )}
    </div>
  );
}
